import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  InputNumber,
  Form,
  Button,
  List,
  Typography,
  Alert,
  FormInstance,
  Collapse,
  Space,
  Tooltip,
} from "antd";
import { Phase } from "@design-system";
import dayjs from "dayjs";
import styles from "./customise-timeline-modal.module.css";
import {
  calculateEvenDistributionPercentages,
  calculateEvenTaskDistributionPercentages,
  calculateProportionateDistributionPercentages,
  hasTimeEstimates,
} from "./timeline-distribution-utils";

const { Text } = Typography;
const { Panel } = Collapse;

const {
  ["scrollable-container"]: scrollableContainerClassName,
  ["phase-panel"]: phasePanelClassName,
  ["task-list"]: taskListClassName,
} = styles as any;

interface TaskPercentage {
  taskId: number;
  name: string;
  percentage: number | null;
}

interface PhasePercentage {
  phaseId: number;
  name: string;
  percentage: number | null;
  tasks: TaskPercentage[];
}

interface CustomiseFormValues {
  [key: string]: number | null;
}

export interface CustomTimelineSaveData {
  phaseId: number;
  phasePercentage: number;
  taskPercentages: { taskId: number; percentage: number }[];
}

type Props = {
  phases: Phase[];
  visible: boolean;
  onClose: () => void;
  onSave: (data: CustomTimelineSaveData[]) => void;
  matterStartDate: Date | null;
  matterDeadlineDate: Date | null;
};

export const CustomiseTimelineModal: React.FC<Props> = ({
  phases,
  visible,
  onClose,
  onSave,
  matterStartDate,
  matterDeadlineDate,
}) => {
  const [form]: [FormInstance<CustomiseFormValues>] = Form.useForm();
  const [phaseStructure, setPhaseStructure] = useState<PhasePercentage[]>([]);
  const watchedValues = Form.useWatch<CustomiseFormValues>([], form);
  const prevWatchedValuesRef = useRef<CustomiseFormValues | undefined>(undefined);

  useEffect(() => {
    if (visible && phases.length > 0) {
      let initialData: PhasePercentage[] = [];
      const matterTotalDurationSeconds =
        matterStartDate && matterDeadlineDate
          ? dayjs(matterDeadlineDate).diff(matterStartDate, "second")
          : 0;

      initialData = phases.map((phase, index) => {
        let phasePercentage: number | null = 0;
        if (matterTotalDurationSeconds > 0 && phase.startDate && phase.deadlineDate) {
          const phaseDurationSeconds = dayjs(phase.deadlineDate).diff(phase.startDate, "second");
          if (phaseDurationSeconds > 0) {
            phasePercentage = parseFloat(
              ((phaseDurationSeconds / matterTotalDurationSeconds) * 100).toFixed(2)
            );
          }
        }

        let initialTaskData: TaskPercentage[] = [];
        const phaseDurationSeconds =
          phase.startDate && phase.deadlineDate
            ? dayjs(phase.deadlineDate).diff(phase.startDate, "second")
            : 0;

        if (phase.tasks && phase.tasks.length > 0) {
          if (phaseDurationSeconds > 0) {
            initialTaskData = phase.tasks.map((task, taskIndex) => {
              let taskPercentage: number | null = 0;
              if (task.startDate && task.deadlineDate) {
                const taskDurationSeconds = dayjs(task.deadlineDate).diff(task.startDate, "second");
                if (taskDurationSeconds > 0) {
                  taskPercentage = parseFloat(
                    ((taskDurationSeconds / phaseDurationSeconds) * 100).toFixed(2)
                  );
                }
              }
              return {
                taskId: task.id,
                name: task.title || `Task ${taskIndex + 1}`,
                percentage: taskPercentage,
              };
            });

            const totalTaskPercentage = initialTaskData.reduce(
              (sum, t) => sum + (t.percentage || 0),
              0
            );
            if (totalTaskPercentage > 100.01) {
              const evenTaskPercentage = parseFloat((100 / phase.tasks.length).toFixed(2));
              initialTaskData = phase.tasks.map((task, taskIndex) => ({
                taskId: task.id,
                name: task.title || `Task ${taskIndex + 1}`,
                percentage:
                  taskIndex === phase.tasks!.length - 1
                    ? parseFloat((100 - evenTaskPercentage * (phase.tasks!.length - 1)).toFixed(2))
                    : evenTaskPercentage,
              }));
            }
          } else {
            const evenTaskPercentage = parseFloat((100 / phase.tasks.length).toFixed(2));
            initialTaskData = phase.tasks.map((task, taskIndex) => ({
              taskId: task.id,
              name: task.title || `Task ${taskIndex + 1}`,
              percentage:
                taskIndex === phase.tasks!.length - 1
                  ? parseFloat((100 - evenTaskPercentage * (phase.tasks!.length - 1)).toFixed(2))
                  : evenTaskPercentage,
            }));
          }
        }

        return {
          phaseId: phase.id,
          name: phase.title || `Phase ${index + 1}`,
          percentage: phasePercentage,
          tasks: initialTaskData,
        };
      });

      const calculatedPhaseTotal = initialData.reduce((sum, p) => sum + (p.percentage || 0), 0);
      if (calculatedPhaseTotal > 100.01) {
        initialData = initialData.map((p) => ({ ...p, percentage: 0 }));
      }

      setPhaseStructure(initialData);

      const initialFormValues = initialData.reduce<CustomiseFormValues>((acc, phase) => {
        acc[`percentage_phase_${phase.phaseId}`] = phase.percentage;
        phase.tasks.forEach((task) => {
          acc[`percentage_task_${task.taskId}`] = task.percentage;
        });
        return acc;
      }, {});
      form.setFieldsValue(initialFormValues);
    } else if (!visible) {
      form.resetFields();
      setPhaseStructure([]);
    }
  }, [visible, phases, form, matterStartDate, matterDeadlineDate]);

  useEffect(() => {
    if (!watchedValues || !prevWatchedValuesRef.current) {
      prevWatchedValuesRef.current = watchedValues;
      return;
    }

    const currentValues = watchedValues;
    const previousValues = prevWatchedValuesRef.current;
    let taskUpdates: CustomiseFormValues = {};
    let needsUpdate = false;

    phaseStructure.forEach((phase) => {
      if (phase.tasks.length > 0) {
        const phaseKey = `percentage_phase_${phase.phaseId}`;
        const currentPhasePercent = Number(currentValues[phaseKey]) || 0;
        const prevPhasePercent = Number(previousValues[phaseKey]) || 0;

        if (prevPhasePercent === 0 && currentPhasePercent > 0) {
          needsUpdate = true;
          const evenTaskPercentage = parseFloat((100 / phase.tasks.length).toFixed(2));
          phase.tasks.forEach((task, index) => {
            const taskKey = `percentage_task_${task.taskId}`;
            taskUpdates[taskKey] =
              index === phase.tasks.length - 1
                ? parseFloat((100 - evenTaskPercentage * (phase.tasks.length - 1)).toFixed(2))
                : evenTaskPercentage;
          });
        }
      }
    });

    if (needsUpdate) {
      form.setFieldsValue(taskUpdates);
    }

    prevWatchedValuesRef.current = currentValues;
  }, [watchedValues, phaseStructure, form]);

  const totalPhasePercentage = useMemo(() => {
    const currentValues = watchedValues || {};
    return parseFloat(
      Object.keys(currentValues)
        .filter((key) => key.startsWith("percentage_phase_"))
        .reduce((sum, key) => sum + (Number(currentValues[key]) || 0), 0)
        .toFixed(2)
    );
  }, [watchedValues]);

  // Check if any phase's works have time values > 0
  const hasPhaseTimeEstimates = useMemo(() => hasTimeEstimates(phases), [phases]);

  // Function to distribute phase percentages evenly
  const handleEvenDistribution = () => {
    if (phases.length === 0) return;

    const phaseDistribution = calculateEvenDistributionPercentages(phases);
    const phaseValues: CustomiseFormValues = {};

    phaseDistribution.forEach(({ phaseId, percentage }) => {
      phaseValues[`percentage_phase_${phaseId}`] = percentage;
    });

    // Calculate even distribution for tasks in each phase
    phases.forEach((phase) => {
      if (phase.tasks && phase.tasks.length > 0) {
        const taskDistribution = calculateEvenTaskDistributionPercentages(phase);

        taskDistribution.forEach(({ taskId, percentage }) => {
          phaseValues[`percentage_task_${taskId}`] = percentage;
        });
      }
    });

    form.setFieldsValue(phaseValues);
  };

  // Function to distribute phase percentages proportionate to time estimates
  const handleProportionateDistribution = () => {
    if (phases.length === 0) return;

    const phaseDistribution = calculateProportionateDistributionPercentages(phases);
    const phaseValues: CustomiseFormValues = {};

    phaseDistribution.forEach(({ phaseId, percentage }) => {
      phaseValues[`percentage_phase_${phaseId}`] = percentage;
    });

    // Calculate even distribution for tasks in each phase
    phases.forEach((phase) => {
      if (phase.tasks && phase.tasks.length > 0) {
        const taskDistribution = calculateEvenTaskDistributionPercentages(phase);

        taskDistribution.forEach(({ taskId, percentage }) => {
          phaseValues[`percentage_task_${taskId}`] = percentage;
        });
      }
    });

    form.setFieldsValue(phaseValues);
  };

  const calculatePhaseTaskTotal = (phaseId: number, tasks: TaskPercentage[]): number => {
    const currentValues = watchedValues || {};
    const total = tasks.reduce((sum, task) => {
      const key = `percentage_task_${task.taskId}`;
      return sum + (Number(currentValues[key]) || 0);
    }, 0);
    return parseFloat(total.toFixed(2));
  };

  const handleSave = () => {
    if (totalPhasePercentage <= 100) {
      const formValues = form.getFieldsValue();
      const result: CustomTimelineSaveData[] = phaseStructure.map((phase) => ({
        phaseId: phase.phaseId,
        phasePercentage: Number(formValues?.[`percentage_phase_${phase.phaseId}`]) || 0,
        taskPercentages: phase.tasks.map((task) => ({
          taskId: task.taskId,
          percentage: Number(formValues?.[`percentage_task_${task.taskId}`]) || 0,
        })),
      }));
      onSave(result);
    }
  };

  const isSaveDisabled = totalPhasePercentage > 100;

  return (
    <Modal
      title="Customise Phase & Task Durations (%)"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSave} disabled={isSaveDisabled}>
          Save
        </Button>,
      ]}
      width={700}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Space style={{ marginBottom: "16px" }} size="middle">
          <Button type="default" onClick={handleEvenDistribution}>
            Distribute Evenly
          </Button>
          {hasPhaseTimeEstimates ? (
            <Button type="default" onClick={handleProportionateDistribution}>
              Distribute By Time Estimates
            </Button>
          ) : (
            <Tooltip title="No time estimates found in phases">
              <Button type="default" disabled>
                Distribute By Time Estimates
              </Button>
            </Tooltip>
          )}
        </Space>

        <div className={scrollableContainerClassName}>
          <Collapse accordion ghost expandIconPosition="right">
            {phaseStructure.map((phase) => {
              const phaseTaskTotal = calculatePhaseTaskTotal(phase.phaseId, phase.tasks);
              const taskTotalColor =
                phaseTaskTotal > 100 ? "red" : phaseTaskTotal < 99.9 ? "orange" : "inherit";

              const phasePercentageValue =
                Number(watchedValues?.[`percentage_phase_${phase.phaseId}`]) || 0;

              return (
                <Panel
                  key={phase.phaseId}
                  className={phasePanelClassName}
                  collapsible={phasePercentageValue === 0 ? "disabled" : undefined}
                  header={
                    <Space
                      align="center"
                      style={{
                        width: "100%",
                        justifyContent: "space-between",
                        marginLeft: "-4px",
                      }}
                    >
                      <Text
                        style={{
                          flexGrow: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                        ellipsis
                      >
                        {phase.name}
                      </Text>
                      <Form.Item
                        name={`percentage_phase_${phase.phaseId}`}
                        noStyle
                        rules={[{ type: "number", min: 0, max: 100 }]}
                      >
                        <InputNumber<number>
                          min={0}
                          max={100}
                          step={0.01}
                          formatter={(value) => `${value}%`}
                          parser={(value) => parseFloat(value?.replace("%", "") || "0")}
                          style={{ width: "100px", marginRight: "16px" }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Form.Item>
                    </Space>
                  }
                >
                  {phase.tasks.length > 0 ? (
                    <>
                      <div
                        style={{
                          padding: "0 16px 8px 16px",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Text type="secondary" style={{ color: taskTotalColor }}>
                          Tasks Total: {phaseTaskTotal}%
                        </Text>
                        {phaseTaskTotal > 100 && (
                          <Text type="danger" style={{ marginLeft: "8px" }}>
                            (Exceeds 100%)
                          </Text>
                        )}
                      </div>
                      <List
                        className={taskListClassName}
                        dataSource={phase.tasks}
                        renderItem={(task) => (
                          <List.Item key={task.taskId}>
                            <List.Item.Meta title={<Text ellipsis>{task.name}</Text>} />
                            <Form.Item
                              name={`percentage_task_${task.taskId}`}
                              noStyle
                              rules={[{ type: "number", min: 0, max: 100 }]}
                            >
                              <InputNumber<number>
                                min={0}
                                max={100}
                                step={0.01}
                                formatter={(value) => `${value}%`}
                                parser={(value) => parseFloat(value?.replace("%", "") || "0")}
                                style={{ width: "100px" }}
                              />
                            </Form.Item>
                          </List.Item>
                        )}
                      />
                    </>
                  ) : (
                    <div style={{ padding: "16px", textAlign: "center" }}>
                      <Text type="secondary">No tasks in this phase.</Text>
                    </div>
                  )}
                </Panel>
              );
            })}
          </Collapse>
        </div>
        <Form.Item
          style={{
            marginTop: "16px",
            borderTop: "1px solid #f0f0f0",
            paddingTop: "16px",
            marginBottom: "0px",
          }}
        >
          <Text strong>Total Phase Percentage: {totalPhasePercentage}%</Text>
          {totalPhasePercentage > 100 && (
            <Alert
              message="Total Phase percentage cannot exceed 100%."
              type="error"
              showIcon
              style={{ marginTop: "10px" }}
            />
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};
